import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from 'styletron-react';
import { Button, KIND, SIZE } from 'baseui/button';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Spinner } from 'baseui/spinner';
import { Drawer, SIZE as DRAWER_SIZE } from 'baseui/drawer';
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Download, Edit3, ExternalLink, RefreshCw, Search, X } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useEvent, useEventAccesses } from '../../hooks/useEvents';
import { useNationalities, formatNationality } from '../../hooks/useNationalities';
import { needsStandaloneAccessSettings, useAccessSettings } from '../../hooks/useAccessSettings';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { worksAsFrontDesk } from '../../utils/frontDeskMatrix';
import { useAccessStatusLabelMap, getAccessTypeLabel, AccessLabelMap } from '../../hooks/useAccessTypeOptions';
import { slugify } from '../../utils/slug';
import { S3Image } from '../../components/S3Image';
import { InvitationProvider } from '../invitation/context/InvitationContext';
import { ConfirmationView } from '../invitation/components/ConfirmationView';
import { GuestsView } from '../invitation/Invitations';
import { DateField } from '../../components/ui/DateTimeField';
import {
  attendeeSubtitle, companionCounts, mainGuestsByAccess, statusChipStyle,
} from './attendees';
import { useT } from '../../i18n/admin';
import { useFdCan } from '../../hooks/useFdCan';

const SelectComponent = Select as any;
const InputComponent = Input as any;

const Page = styled('div', {
  // Full available height + own scroll so the dashboard is scrollable inside
  // the layout's overflow:hidden frame.
  height: '100%',
  width: '100%',
  overflow: 'auto',
  background: '#F7F7F7',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxSizing: 'border-box',
  '@media (max-width: 640px)': {
    padding: '14px',
    gap: '14px',
  },
});

const TopBar = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});

const Heading = styled('h1', {
  margin: 0,
  fontSize: '22px',
  fontWeight: 700,
  color: '#111827',
  // Let the title shrink and ellipsize instead of pushing the action buttons
  // off-screen on narrow viewports.
  flex: '1 1 200px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '@media (max-width: 640px)': {
    fontSize: '18px',
    flexBasis: '100%',
  },
});

const Subheading = styled('div', {
  fontSize: '14px',
  color: '#6B7280',
});

const CardsRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '12px',
});

const Card = styled('div', {
  backgroundColor: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const CardLabel = styled('div', {
  fontSize: '12px',
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 600,
});

const CardValue = styled('div', {
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
});

const ChartsRow = styled('div', {
  display: 'grid',
  // Three equal columns that stretch to fill the row (no trailing gap). The
  // full-width charts span all columns via gridColumn: '1 / -1'. Collapses to
  // two, then one column on narrower viewports.
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '16px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  '@media (max-width: 680px)': {
    gridTemplateColumns: '1fr',
  },
});

const ChartCard = styled('div', {
  backgroundColor: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}) as any;

const ChartHead = styled('div', {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '12px',
  flexWrap: 'wrap',
});

const ChartHint = styled('div', {
  fontSize: '12px',
  color: '#6B7280',
  fontWeight: 500,
});

const EmptyChart = styled('div', {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9CA3AF',
  fontSize: '13px',
  background: '#F9FAFB',
  border: '1px dashed #E5E7EB',
  borderRadius: '8px',
});

const FiltersRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 1fr) repeat(2, minmax(160px, 200px)) minmax(140px, 200px) auto',
  gap: '12px',
  alignItems: 'end',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr 1fr',
  },
  '@media (max-width: 560px)': {
    gridTemplateColumns: '1fr',
  },
});

const ChipRow = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

const Chip = styled('button', ({ $active }: { $active?: boolean }) => ({
  border: '1px solid',
  borderColor: $active ? 'var(--tenant-primary, #111827)' : '#E5E7EB',
  background: $active ? 'var(--tenant-primary, #111827)' : '#fff',
  color: $active ? '#fff' : '#374151',
  padding: '6px 12px',
  borderRadius: '999px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
})) as any;

const TableWrap = styled('div', {
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  // Page is a fixed-height flex column; without this the overflow:auto box
  // shrinks under flex pressure and clips the table to near-zero height.
  flexShrink: 0,
});

const Table = styled('table', {
  width: '100%',
  minWidth: '640px',
  borderCollapse: 'separate',
  borderSpacing: 0,
  background: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  overflow: 'hidden',
});

const Th = styled('th', {
  textAlign: 'left',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#6B7280',
  fontWeight: 600,
  padding: '12px 14px',
  background: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB',
});

const Td = styled('td', {
  padding: '10px 14px',
  fontSize: '14px',
  color: '#111827',
  borderBottom: '1px solid #F3F4F6',
});

const Tr = styled('tr', {
  cursor: 'pointer',
  ':hover': { background: '#F9FAFB' },
}) as any;

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// SAM-581: status labels are resolved from the live business_access_status
// records (useAccessStatusLabelMap), not a hardcoded, best-effort guess —
// this file used to disagree with the dashboard/invitations labels for the
// same id. `statusLabel` here takes the live map explicitly since it is
// also called from downloadCSV, a plain function outside the component.
const statusLabel = (map: AccessLabelMap | undefined, id: any) => getAccessTypeLabel(map, id) || `Status ${id ?? '?'}`;

interface PersonRow {
  accessId: string;
  accessStatusId: number;
  createDate: string;
  createdAt: string;
  isMainGuest: boolean;
  categoryKey: string;
  categoryLabel: string;
  name: string;
  lastName: string;
  idnum?: string;
  nationality?: string;
  photoUrl?: string;
}

// Extract the hour-of-day (0–23) from a stored timestamp without letting JS
// Date shift it across timezones — we read the HH straight off the string so
// the chart reflects the time as the backend stored it. Returns null when the
// value carries no time component (date-only records).
const hourOf = (raw: string | undefined): number | null => {
  if (!raw) return null;
  const m = raw.match(/[T ](\d{2}):/);
  if (!m) return null;
  const h = Number(m[1]);
  return h >= 0 && h <= 23 ? h : null;
};

const downloadCSV = (rows: PersonRow[], filename: string, statusLabelMap: AccessLabelMap | undefined) => {
  const header = ['name', 'last_name', 'idnum', 'category', 'status', 'access_id', 'create_date'];
  const escape = (v: any) => {
    const s = (v ?? '').toString();
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    header.join(','),
    ...rows.map(r => [
      r.name,
      r.lastName,
      r.idnum || '',
      r.categoryLabel,
      statusLabel(statusLabelMap, r.accessStatusId),
      r.accessId,
      r.createDate,
    ].map(escape).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const EventDashboard: React.FC = () => {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { fmt } = useT();
  const permissions = useUserPermissions();
  const { isEventDashboardOnly } = permissions;
  // The estate group roles cannot list access_settings either (SAM-1108).
  const asFrontDesk = worksAsFrontDesk(permissions);
  const canEditEvent = useFdCan('events', 'edit');
  const { data: event, isLoading: eventLoading } = useEvent(eventId);
  const { data: accesses = [], isLoading: accessesLoading, refetch: refetchAccesses, isFetching: accessesFetching } = useEventAccesses(eventId);
  // Backup: when the event response doesn't embed access_settings (some
  // backend variants don't honor `?expand=access_settings`), look it up by id
  // from the cached access_settings list. Only when the event came without it, and never for the tenant Front Desk,
  // which cannot list the table (403): it gets access_settings as an expand of the event.
  const { data: allAccessSettings = [] } = useAccessSettings({
    enabled: needsStandaloneAccessSettings({ eventLoaded: !!event, inlineSettings: event?.access_settings, isTenantFrontDesk: asFrontDesk }),
  });
  const { map: statusLabelMap } = useAccessStatusLabelMap();

  // Filters. categoryFilter holds the category KEY (e.g. "athlete"), not the
  // label, so configured categories stay distinct even if the admin renames
  // their display labels. Legacy records without `category_key` still bucket
  // together under their default key ('adults').
  const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Frontend pagination — 10 rows per page is enough for any reasonable event.
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  // Detail drawer — shows the access info + shareable link when a row is clicked.
  // Mirrors the Invitations page drawer (Details + Guests tabs) so business_access
  // detail looks the same wherever it appears.
  const [selectedAccessId, setSelectedAccessId] = useState<string | null>(null);
  const [drawerTab, setDrawerTab] = useState<'details' | 'qr'>('details');
  // SAM-1109: the person whose row opened the pass, so Guests can mark them.
  const [openedPerson, setOpenedPerson] = useState<PersonRow | null>(null);

  // Opt-in live refresh — polls the accesses every 30s while enabled so the
  // dashboard reflects new registrations without a manual reload.
  const [autoRefresh, setAutoRefresh] = useState(false);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => { refetchAccesses(); }, 30000);
    return () => window.clearInterval(id);
  }, [autoRefresh, refetchAccesses]);
  const selectedAccess = useMemo(
    () => accesses.find((a: any) => a?.id === selectedAccessId) || null,
    [accesses, selectedAccessId],
  );
  const closeAccessDrawer = () => { setSelectedAccessId(null); setDrawerTab('details'); setOpenedPerson(null); };

  // ---- Derive template + category labels ----
  // Prefer the access_settings inlined on the event response. If that's not
  // there, fall back to the standalone access_settings list (matched by id).
  const inlineSettings = event?.access_settings;
  const settingsId = (event?.access_settings_id as number | undefined) ?? (inlineSettings?.id as number | undefined);
  const fallbackSettings = settingsId
    ? (allAccessSettings as any[]).find((s: any) => Number(s?.id) === Number(settingsId))
    : undefined;
  const accessSettings = inlineSettings || fallbackSettings;
  const template = accessSettings?.settings?.event?.[0];
  const customCategories: any[] = template?.categories || [];
  const mainCat = customCategories.find((c: any) => c?.is_main_guest);
  // Built-in adults/child slot keys are derived from their labels (same as
  // custom categories) so renaming the label produces a distinct bucket.
  // Important: when the template's label is *missing*, fall back to the literal
  // 'adults'/'child' instead of slugifying the placeholder ("Adult" → "adult"),
  // so legacy records with `category_key: "adults"` and dashboard fallbacks
  // collapse into the same bucket.
  const adultsLabel = template?.adults?.title_en || 'Adult';
  const adultsKey = template?.adults?.title_en
    ? (slugify(template.adults.title_en) || 'adults')
    : 'adults';
  const childLabel = template?.child?.name_en || 'Children';
  const childKey = template?.child?.name_en
    ? (slugify(template.child.name_en) || 'child')
    : 'child';

  const labelFor = (key: string): string => {
    if (key === adultsKey || key === 'adults') return adultsLabel;
    if (key === childKey || key === 'child') return childLabel;
    const cat = customCategories.find((c: any) => c?.key === key);
    return cat?.title_en || cat?.title_es || key;
  };

  // ---- Flatten accesses → persons ----
  const allPersons = useMemo<PersonRow[]>(() => {
    const rows: PersonRow[] = [];
    for (const acc of accesses) {
      const accessId = acc?.id;
      const accessStatusId = Number(acc?.business_access_status_id ?? 0);
      const createdAt = (acc?.create_date || '').toString();
      const createDate = createdAt.slice(0, 10);
      const main = acc?.main_guest || {};

      // Main guest's category — three-way fallback:
      //   1. Explicit category_key on the persisted main_guest JSONB (best;
      //      written by the public events flow at registration time).
      //   2. The is_main_guest-marked category from the access_settings template.
      //   3. The adults bucket (when nothing better is available).
      const mainKey = (main && (main.category_key as string)) || mainCat?.key || adultsKey;
      rows.push({
        accessId,
        accessStatusId,
        createDate,
        createdAt,
        isMainGuest: true,
        categoryKey: mainKey,
        categoryLabel: labelFor(mainKey),
        name: main?.name || '',
        lastName: main?.last_name || '',
        idnum: main?.idnum || '',
        nationality: main?.nationality || '',
        photoUrl: main?.photo_url || '',
      });

      const guests = Array.isArray(acc?.business_guests) ? acc.business_guests : [];
      for (const g of guests) {
        const key = g?.category_key || adultsKey;
        rows.push({
          accessId,
          accessStatusId,
          createDate,
          createdAt,
          isMainGuest: false,
          categoryKey: key,
          categoryLabel: labelFor(key),
          name: g?.name || '',
          lastName: g?.last_name || '',
          idnum: g?.idnum || '',
          nationality: g?.nationality || '',
          photoUrl: g?.photo_url || '',
        });
      }
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accesses, template, customCategories.length]);

  const mainsByAccess = useMemo(() => mainGuestsByAccess(allPersons), [allPersons]);
  const companionsByAccess = useMemo(() => companionCounts(allPersons), [allPersons]);

  const totalChildren = useMemo(
    () => accesses.reduce((sum: number, a: any) => sum + Number(a?.children_count || 0), 0),
    [accesses],
  );
  const totalAccesses = accesses.length;
  const confirmedAccesses = accesses.filter((a: any) => Number(a?.business_access_status_id) === 2).length;
  const pendingAccesses = accesses.filter((a: any) => Number(a?.business_access_status_id) === 1).length;
  const totalPeople = allPersons.length + totalChildren;

  // ---- Apply filters to the persons list ----
  const filteredPersons = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return allPersons.filter(p => {
      if (statusFilter !== 'all' && p.accessStatusId !== statusFilter) return false;
      if (categoryFilter !== 'all' && p.categoryKey !== categoryFilter) return false;
      if (q && !`${p.name} ${p.lastName} ${p.idnum || ''}`.toLowerCase().includes(q)) return false;
      if (dateFrom && p.createDate && p.createDate < dateFrom) return false;
      if (dateTo && p.createDate && p.createDate > dateTo) return false;
      return true;
    });
  }, [allPersons, statusFilter, categoryFilter, searchText, dateFrom, dateTo]);

  // Reset to page 1 whenever the filtered list changes shape so we don't end
  // up showing an empty page when the result count drops.
  useEffect(() => { setPage(1); }, [statusFilter, categoryFilter, searchText, dateFrom, dateTo, allPersons.length]);

  const totalPages = Math.max(1, Math.ceil(filteredPersons.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedPersons = filteredPersons.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ---- Aggregate counts per category for the charts ----
  // Grouped by KEY (so configured categories stay distinct even if labels are
  // edited later). The chart still displays the label resolved via labelFor().
  const categoryAggregate = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of filteredPersons) {
      counts[p.categoryKey] = (counts[p.categoryKey] || 0) + 1;
    }
    if (totalChildren > 0 && categoryFilter === 'all') {
      counts[childKey] = (counts[childKey] || 0) + totalChildren;
    }
    return Object.entries(counts).map(([key, value]) => ({
      key, value, label: labelFor(key),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPersons, totalChildren, categoryFilter]);

  const categoryOptions = useMemo(() => {
    const keys = new Set<string>();
    for (const p of allPersons) keys.add(p.categoryKey);
    if (totalChildren > 0) keys.add(childKey);
    return Array.from(keys).map(key => ({ key, label: labelFor(key) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPersons, totalChildren]);

  // ---- Registrations by hour of day (peak hours) ----
  // Always renders the full 0–23 axis so the daily shape (peaks/valleys) is
  // readable even when some hours have zero registrations.
  const { data: nationalitiesList } = useNationalities();

  const byHour = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: `${String(h).padStart(2, '0')}:00`,
      value: 0,
    }));
    for (const p of filteredPersons) {
      const h = hourOf(p.createdAt);
      if (h != null) buckets[h].value += 1;
    }
    return buckets;
  }, [filteredPersons]);

  const peakHour = useMemo(
    () => byHour.reduce((best, b) => (b.value > best.value ? b : best), byHour[0]),
    [byHour],
  );

  // ---- Registrations per day ----
  // Counts people by registration day, sorted chronologically. Useful for
  // multi-day events to see which day drove the most sign-ups.
  const byDay = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of filteredPersons) {
      const d = (p.createDate || '').slice(0, 10);
      if (d) counts[d] = (counts[d] || 0) + 1;
    }
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, value]) => ({
        day,
        // Short, locale-agnostic MM-DD label keeps the axis compact.
        label: day.slice(5),
        value,
      }));
  }, [filteredPersons]);

  // ---- Top nationalities ----
  // Resolves each stored value (ISO code / country name / legacy flag URL) to a
  // display country name before counting, then keeps the top 8.
  const topNationalities = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of filteredPersons) {
      const raw = (p.nationality || '').trim();
      if (!raw) continue;
      const label = formatNationality(raw, nationalitiesList);
      if (!label || label === '—') continue;
      counts[label] = (counts[label] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [filteredPersons, nationalitiesList]);

  const withNationality = useMemo(
    () => filteredPersons.filter(p => (p.nationality || '').trim()).length,
    [filteredPersons],
  );

  const isLoading = eventLoading || accessesLoading;

  return (
    <Page>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <TopBar>
        <Button size={SIZE.compact} kind={KIND.tertiary} onClick={() => navigate('/eventsad/list')}>
          <ArrowLeft size={16} style={{ marginRight: 6 }} /> Back to events
        </Button>
        <Heading>{event?.name || event?.name_en || 'Event dashboard'}</Heading>
        <div style={{ flex: 1 }} />
        <Button
          size={SIZE.compact}
          kind={autoRefresh ? KIND.primary : KIND.secondary}
          onClick={() => setAutoRefresh(v => !v)}
          title="Refresh data every 30 seconds"
          overrides={autoRefresh ? { BaseButton: { style: { backgroundColor: 'var(--tenant-primary, #111827)' } } } : undefined}
        >
          <RefreshCw
            size={14}
            style={{ marginRight: 6, animation: accessesFetching ? 'spin 0.8s linear infinite' : undefined }}
          />
          {autoRefresh ? 'Auto-refresh on' : 'Auto-refresh'}
        </Button>
        {/* Event-dashboard-only role and the tenant Front Desk (matrix: events are view only) can export but not edit. */}
        {!isEventDashboardOnly && canEditEvent && (
          <Button
            size={SIZE.compact}
            kind={KIND.secondary}
            onClick={() => navigate(`/eventsad/${eventId}`)}
          >
            <Edit3 size={14} style={{ marginRight: 6 }} /> Edit event
          </Button>
        )}
        <Button
          size={SIZE.compact}
          onClick={() => downloadCSV(filteredPersons, `${event?.name || 'event'}-persons.csv`, statusLabelMap)}
          disabled={filteredPersons.length === 0}
          overrides={{ BaseButton: { style: { backgroundColor: 'var(--tenant-primary, #111827)' } } }}
        >
          <Download size={14} style={{ marginRight: 6 }} /> Export CSV
        </Button>
      </TopBar>
      <Subheading>
        {event?.start_date && (
          <>
            {fmt.date(event.start_date)} —{' '}
            {event?.end_date ? fmt.date(event.end_date) : 'TBD'}
          </>
        )}
      </Subheading>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spinner $size={32} />
        </div>
      ) : (
        <>
          {/* ---------- Summary ---------- */}
          <CardsRow>
            <Card>
              <CardLabel>Accesses</CardLabel>
              <CardValue>{totalAccesses}</CardValue>
            </Card>
            <Card>
              <CardLabel>Total people</CardLabel>
              <CardValue>{totalPeople}</CardValue>
            </Card>
            <Card>
              <CardLabel>Confirmed</CardLabel>
              <CardValue>{confirmedAccesses}</CardValue>
            </Card>
            <Card>
              <CardLabel>Pending</CardLabel>
              <CardValue>{pendingAccesses}</CardValue>
            </Card>
            <Card>
              <CardLabel>Children</CardLabel>
              <CardValue>{totalChildren}</CardValue>
            </Card>
          </CardsRow>

          {/* ---------- Charts ---------- */}
          <ChartsRow>
            {/* Registrations by hour — spans the full row so peaks are legible. */}
            <ChartCard style={{ gridColumn: '1 / -1' }}>
              <ChartHead>
                <CardLabel>Registrations by hour</CardLabel>
                {peakHour.value > 0 && (
                  <ChartHint>Peak at {peakHour.label} · {peakHour.value} {peakHour.value === 1 ? 'person' : 'people'}</ChartHint>
                )}
              </ChartHead>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <AreaChart data={byHour} margin={{ left: -16, right: 12, top: 8 }}>
                    <defs>
                      <linearGradient id="hourFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--tenant-primary, #3B82F6)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="var(--tenant-primary, #3B82F6)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                    <XAxis dataKey="label" interval={1} tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                    <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ stroke: '#E5E7EB' }} labelFormatter={(l: any) => `Hour ${l}`} formatter={(v: any) => [v, 'People']} />
                    <Area type="monotone" dataKey="value" stroke="var(--tenant-primary, #3B82F6)" strokeWidth={2} fill="url(#hourFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {/* Registrations per day */}
            <ChartCard style={{ gridColumn: '1 / -1' }}>
              <ChartHead>
                <CardLabel>Registrations per day</CardLabel>
                {byDay.length > 0 && (
                  <ChartHint>{byDay.length} {byDay.length === 1 ? 'day' : 'days'}</ChartHint>
                )}
              </ChartHead>
              <div style={{ width: '100%', height: 220 }}>
                {byDay.length === 0 ? (
                  <EmptyChart>No registrations yet</EmptyChart>
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={byDay} margin={{ left: -16, right: 12, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                      <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: '#F9FAFB' }} labelFormatter={(l: any) => `Day ${l}`} formatter={(v: any) => [v, 'People']} />
                      <Bar dataKey="value" fill="var(--tenant-primary, #3B82F6)" radius={[4, 4, 0, 0]} maxBarSize={64} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </ChartCard>

            <ChartCard>
              <CardLabel>Distribution by category</CardLabel>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={categoryAggregate}
                      dataKey="value"
                      nameKey="label"
                      innerRadius={56}
                      outerRadius={100}
                      paddingAngle={2}
                      label={(entry: any) => `${entry.label}: ${entry.value}`}
                    >
                      {categoryAggregate.map((_, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard>
              <CardLabel>Counts per category</CardLabel>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={categoryAggregate} layout="vertical" margin={{ left: 12, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                    <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 12, fill: '#374151' }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: '#F9FAFB' }} />
                    <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {/* Top nationalities */}
            <ChartCard>
              <ChartHead>
                <CardLabel>Top nationalities</CardLabel>
                <ChartHint>{withNationality} of {filteredPersons.length} with nationality</ChartHint>
              </ChartHead>
              <div style={{ width: '100%', height: 280 }}>
                {topNationalities.length === 0 ? (
                  <EmptyChart>No nationality data yet</EmptyChart>
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={topNationalities} layout="vertical" margin={{ left: 12, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                      <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 12, fill: '#374151' }} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: '#F9FAFB' }} />
                      <Bar dataKey="value" fill="#10B981" radius={[0, 4, 4, 0]}>
                        {topNationalities.map((_, idx) => (
                          <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </ChartCard>
          </ChartsRow>

          {/* ---------- Filters ---------- */}
          <FiltersRow>
            <InputComponent
              size="compact"
              startEnhancer={() => <Search size={14} />}
              value={searchText}
              onChange={(e: any) => setSearchText(e.target.value)}
              placeholder="Search name or ID number"
            />
            <SelectComponent
              size="compact"
              clearable={false}
              options={[
                { id: 'all', label: 'All statuses' },
                { id: 1, label: 'Pending' },
                { id: 2, label: 'Confirmed' },
                { id: 3, label: 'Cancelled' },
              ]}
              value={[{ id: statusFilter, label: statusFilter === 'all' ? 'All statuses' : statusLabel(statusLabelMap, statusFilter) }]}
              onChange={({ value }: any) => setStatusFilter(value[0]?.id ?? 'all')}
            />
            <DateField ariaLabel="From date" placeholder="From" className="w-[170px]" value={dateFrom} onChange={setDateFrom} />
            <DateField ariaLabel="To date" placeholder="To" className="w-[170px]" value={dateTo} onChange={setDateTo} />
            <div style={{ fontSize: 12, color: '#6B7280', alignSelf: 'center' }}>
              {filteredPersons.length} of {allPersons.length}
            </div>
          </FiltersRow>

          <ChipRow>
            <Chip $active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>All</Chip>
            {categoryOptions.map(c => (
              <Chip key={c.key} $active={categoryFilter === c.key} onClick={() => setCategoryFilter(c.key)}>
                {c.label}
              </Chip>
            ))}
          </ChipRow>

          {/* ---------- Persons table ---------- */}
          {filteredPersons.length === 0 ? (
            <div style={{ background: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: 12, padding: 32, textAlign: 'center', color: '#6B7280' }}>
              No people match the current filters.
            </div>
          ) : (
            <>
              <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Photo</Th>
                    <Th>Name</Th>
                    <Th>Category</Th>
                    <Th>ID</Th>
                    <Th>Status</Th>
                    <Th>Date</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPersons.map((p, i) => (
                    <Tr
                      key={`${p.accessId}-${p.isMainGuest ? 'main' : 'g' + i}`}
                      onClick={() => {
                        setSelectedAccessId(p.accessId);
                        // A companion's row opens the pass on Guests with that person marked; the main guest opens Details.
                        setOpenedPerson(p.isMainGuest ? null : p);
                        setDrawerTab(p.isMainGuest ? 'details' : 'qr');
                      }}
                    >
                      <Td>
                        {p.photoUrl ? (
                          <div style={{ width: 36, height: 36, borderRadius: 6, background: '#F3F4F6', overflow: 'hidden' }}>
                            {p.photoUrl.startsWith('http') ? (
                              <img src={p.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              // S3 key — let S3Image resolve a presigned URL
                              <S3Image
                                imageKey={p.photoUrl}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                showLoading={false}
                              />
                            )}
                          </div>
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: 6, background: '#F3F4F6' }} />
                        )}
                      </Td>
                      <Td>
                        <div style={{ fontWeight: 600 }}>{p.name} {p.lastName}</div>
                        {(() => {
                          const sub = attendeeSubtitle(p, mainsByAccess, companionsByAccess);
                          return <div style={{ fontSize: 11, color: sub.companion ? '#005488' : '#6B7280', fontWeight: sub.companion ? 500 : 400 }}>{sub.text}</div>;
                        })()}
                      </Td>
                      <Td>{p.categoryLabel}</Td>
                      <Td>{p.idnum || '—'}</Td>
                      <Td>
                        {(() => {
                          const label = statusLabel(statusLabelMap, p.accessStatusId);
                          const c = statusChipStyle(label);
                          return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{label}</span>;
                        })()}
                      </Td>
                      <Td>{p.createDate ? fmt.date(p.createDate) : '—'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              </TableWrap>

              {/* Pagination — frontend only, 10 per page */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 4px' }}>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredPersons.length)} of {filteredPersons.length}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button
                    size={SIZE.compact}
                    kind={KIND.tertiary}
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <div style={{ fontSize: 13, color: '#374151', minWidth: 80, textAlign: 'center' }}>
                    Page {safePage} of {totalPages}
                  </div>
                  <Button
                    size={SIZE.compact}
                    kind={KIND.tertiary}
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ---------- Access detail drawer ---------- */}
      {selectedAccess && (
        <Drawer
          isOpen={!!selectedAccessId}
          onClose={closeAccessDrawer}
          size={DRAWER_SIZE.auto}
          anchor="right"
          overrides={{
            Root: { style: { zIndex: 10000 } },
            Backdrop: { style: { zIndex: 10000 } },
            DrawerContainer: { style: { width: '40vw', maxWidth: '500px', minWidth: '400px', zIndex: 10001 } },
            DrawerBody: { style: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' } },
          }}
        >
          <InvitationProvider>
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Drawer header — same shape as the Invitations page drawer */}
              <div style={{ padding: '20px 24px 0 24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Pass details</h2>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', margin: '12px 0 0 0', padding: '0 24px' }}>
                <button
                  onClick={() => setDrawerTab('details')}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontWeight: drawerTab === 'details' ? 600 : 400,
                    color: drawerTab === 'details' ? '#000' : '#666',
                    borderBottom: drawerTab === 'details' ? '2px solid #000' : '2px solid transparent',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Details
                </button>
                <button
                  onClick={() => setDrawerTab('qr')}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontWeight: drawerTab === 'qr' ? 600 : 400,
                    color: drawerTab === 'qr' ? '#000' : '#666',
                    borderBottom: drawerTab === 'qr' ? '2px solid #000' : '2px solid transparent',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Guests
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px 24px' }}>
                {drawerTab === 'details' ? (
                  <ConfirmationView
                    onClose={closeAccessDrawer}
                    invitationData={selectedAccess as any}
                    isViewMode={true}
                    onAction={() => { /* dashboard is read-only for now */ }}
                  />
                ) : (
                  <>
                    {openedPerson && (
                      <div style={{ fontSize: 13, color: '#374151', background: '#F3F4F6', borderRadius: 8, padding: '10px 12px', margin: '16px 0 0' }}>
                        {openedPerson.name} {openedPerson.lastName} is a companion on this pass, which belongs to{' '}
                        <b>{(() => { const m = mainsByAccess.get(openedPerson.accessId); return m ? `${m.name} ${m.lastName}`.trim() : 'the main guest'; })()}</b>.
                      </div>
                    )}
                  <GuestsView
                    highlight={openedPerson}
                    invitationData={selectedAccess as any}
                    onShowId={() => { /* lightbox is opened from inside GuestCard */ }}
                  />
                  </>
                )}
              </div>
            </div>
          </InvitationProvider>
        </Drawer>
      )}
    </Page>
  );
};
